import React, { useEffect, useState } from 'react'
import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Button,
  InputGroup,
  Badge,
  Dropdown,
  Spinner,
  Modal
} from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faPlus,
  faSearch,
  faEdit,
  faEye,
  faEllipsisV,
  faSync
} from '@fortawesome/free-solid-svg-icons'
import { Link, useParams, useHistory } from 'react-router-dom'
import { DebounceInput } from 'react-debounce-input'
import Pagination from '../../../../GlobalModules/Utility/Components/Pagination'
import Utility from '../../../../GlobalModules/Utility/Utility'
import { connect } from 'react-redux'
import { withNamespaces } from 'react-i18next'
import { loaderStateTrue, loaderStateFalse, handleActiveLink } from '../../../../Actions/AllAction'
import { getKitchenDisplayList } from '../Controller/KitchenDisplayController'
import '../Assets/css/kitchen-display.scss'

const KitchenDisplayList = (props) => {
  const { lng } = useParams()
  const history = useHistory()

  const [items, setItems] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(25)
  const [sortField, setSortField] = useState('createdAt')
  const [sortDirection, setSortDirection] = useState('desc')
  const [totalItems, setTotalItems] = useState(0)
  const [filters, setFilters] = useState({
    limit: 25,
    offset: 0,
    search: ''
  })

  // Prevent multiple API calls
  const [lastApiCall, setLastApiCall] = useState(null)

  const handleLimitChange = (limit) => {
    const newLimit = parseInt(limit)
    setItemsPerPage(newLimit)
    setFilters((prev) => ({
      ...prev,
      limit: newLimit,
      offset: 0
    }))
  }

  const handleRefresh = () => {
    fetchList();
  };

  const fetchList = async () => {
    // Prevent multiple simultaneous API calls
    const now = Date.now()
    if (lastApiCall && now - lastApiCall < 1000) {
      console.log('Preventing duplicate API call in Kitchen Display List')
      return
    }

    setLastApiCall(now)

    try {
      props.loaderStateTrue()
      let storeId = null

      if (props.userEntryType.user_type == 'staff') {
        if (localStorage.getItem('store_id') == null) {
          localStorage.setItem('store_id', props.userEntryType.branch_id)
        }
        storeId = localStorage.getItem('store_id')
      } else {
        // For admin users, get store_id from user credentials or use default
        storeId =
          props.userCredentials?.store_id || localStorage.getItem('store_id')
      }

      // Add store_id to filters
      const filtersWithStore = {
        ...filters,
        store_id: storeId
      }

      console.log('Fetching with filters:', filtersWithStore) // Debug log
      const response = await getKitchenDisplayList(filtersWithStore)

      if (response.success) {
        setItems(response.data)
        setTotalItems(response.total)
      } else {
        setItems([])
        console.error('API response error:', response)
      }
    } catch (e) {
      setItems([])
      console.error('Fetch error:', e)
    } finally {
      props.loaderStateFalse()
    }
  }

  useEffect(() => {
    fetchList()
  }, [filters])

  const handleSearch = (e) => {
    const value = e.target.value
    setSearchTerm(value)
    const timeoutId = setTimeout(() => {
      setFilters((prev) => ({
        ...prev,
        search: value,
        offset: 0
      }))
    }, 500)
    return () => clearTimeout(timeoutId)
  }

  const handlePageChange = (page) => {
    setCurrentPage(page)
    setFilters((prev) => ({
      ...prev,
      offset: (page - 1) * filters.limit
    }))
  }

  const handleSort = (field) => {
    const newSortDirection =
      sortField === field && sortDirection === 'asc' ? 'desc' : 'asc'

    if (sortField !== field) {
      setSortField(field)
      setSortDirection('asc')
    } else {
      setSortDirection(newSortDirection)
    }

    // Map field names to API column names (camelCase)
    const fieldMapping = {
      kitchen_display_code: 'kitchen_display_code',
      name: 'label',
      createdAt: 'createdAt',
      updatedAt: 'updatedAt'
    }

    const apiField = fieldMapping[field] || field

    setFilters((prev) => ({
      ...prev,
      sort: [
        {
          field: apiField,
          sort: newSortDirection
        }
      ]
    }))
  }

  //======= add action permission ====//
  // Check multiple permissions at once
  const kitchenDisplayPermissions = Utility.hasMultipleActionPermissions(
    'settings',
    'kitchen_displays',
    ['view', 'create', 'edit', 'delete', 'export', 'approve', 'receive', 'verify', 'reject', 'verify_accept'],
    props?.userCredentials?.user_details?.user_type || ['admin', 'app_admin']
  );

  // Check if any actions are available for display in the table
  const hamburgerTableActions = kitchenDisplayPermissions.view || kitchenDisplayPermissions.edit || kitchenDisplayPermissions.delete;

  return (
    <Container fluid className='p-0'>
      <div className='d-flex justify-content-between align-items-center top-header-box'>
        <h4>Kitchen Displays</h4>
        <div>
          {kitchenDisplayPermissions.create && (
            <Button
              variant='primary'
              onClick={() =>
                history.push(`/${lng}/settings/kitchen-displays/add`)
              }
            >
              <FontAwesomeIcon icon={faPlus} className='me-2' /> New Kitchen
              Display
            </Button>
          )}
        </div>
      </div>

      <Card className='custom-border-light'>
        <Card.Body>
          <Row className="mb-0 d-flex justify-content-end">
            <Col md={4} className='mb-3'>
              <InputGroup className='input-group input-group-sm'>
                <InputGroup.Text>
                  <FontAwesomeIcon icon={faSearch} />
                </InputGroup.Text>
                <DebounceInput
                  minLength={1}
                  debounceTimeout={500}
                  className='form-control'
                  placeholder='Search by code, name, or status'
                  value={searchTerm || ''}
                  onChange={(e) => handleSearch(e)}
                  onKeyDown={(e) => {
                    if (e.key === ' ') e.preventDefault()
                  }}
                />
                <div className='ms-2'>
                  <Button
                    variant='outline-secondary'
                    size='sm'
                    onClick={handleRefresh}
                    disabled={props.loaderState}
                    title='Refresh'
                  >
                    <FontAwesomeIcon
                      icon={faSync}
                      spin={props.loaderState}
                    />
                  </Button>
                </div>
              </InputGroup>
            </Col>
          </Row>
          <div className='table-responsive'>
            <Table hover className='align-middle'>
              <thead>
                <tr>
                  <th
                    onClick={() => handleSort('kitchen_display_code')}
                    style={{ cursor: 'pointer' }}
                  >
                    Kitchen Display Code
                  </th>
                  <th
                    onClick={() => handleSort('name')}
                    style={{ cursor: 'pointer' }}
                  >
                    Name
                  </th>
                  <th>Status</th>
                  <th
                    onClick={() => handleSort('createdAt')}
                    style={{ cursor: 'pointer' }}
                  >
                    Created On
                  </th>
                  <th
                    onClick={() => handleSort('updatedAt')}
                    style={{ cursor: 'pointer' }}
                  >
                    Updated On
                  </th>
                  <th>Created By</th>
                  {hamburgerTableActions && <th style={{ minWidth: '100px' }}>Action</th>}
                </tr>
              </thead>
              <tbody>
                {props.loaderState ? (
                  <tr>
                    <td colSpan={hamburgerTableActions ? '8' : '7'} className='text-center py-4'>
                      {/* <Spinner animation='border' variant='primary' />
                      <span className='ms-2'>Loading...</span> */}
                    </td>
                  </tr>
                ) : items.length > 0 ? (
                  items.map((item) => (
                    <tr
                      key={item.id}
                      style={{ cursor: 'pointer' }}
                      onDoubleClick={() => history.push(`/${lng}/settings/kitchen-displays/view/${item.slack}`)}
                    >
                      <td>{item.code || '-'}</td>
                      <td>
                        {item.name || '-'}
                        {Boolean(item.is_default) && (
                          <Badge bg='primary' className='ms-2'>
                            Default
                          </Badge>
                        )}
                      </td>
                      <td>
                        <Badge bg={item.status == 1 ? 'success' : 'secondary'}>
                          {item.status_data?.label ||
                            (item.status == 1 ? 'Active' : 'Inactive')}
                        </Badge>
                      </td>
                      <td>{Utility.get12HourFormat(item.createdAt) || '-'}</td>
                      <td>{Utility.get12HourFormat(item.updatedAt) || '-'}</td>
                      <td>{item.created_by?.fullname || 'Mettle Tech'}</td>
                      {hamburgerTableActions && (
                        <td>
                          <Dropdown align='end' className='action-dropdown'>
                            <Dropdown.Toggle
                              variant='light'
                              size='sm'
                              id={`dropdown-${item.id}`}
                              className='btn-icon no-caret'
                            >
                              <FontAwesomeIcon icon={faEllipsisV} />
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                              {/* {kitchenDisplayPermissions.view && ( */}
                                <Dropdown.Item
                                  as={Link}
                                  to={`/${lng}/settings/kitchen-displays/view/${item.slack}`}
                                >
                                  <FontAwesomeIcon icon={faEye} className='me-2' />{' '}
                                  View
                                </Dropdown.Item>
                              {/* )} */}
                              {kitchenDisplayPermissions.edit && (
                                <Dropdown.Item
                                  as={Link}
                                  to={`/${lng}/settings/kitchen-displays/edit/${item.slack}`}
                                >
                                  <FontAwesomeIcon icon={faEdit} className='me-2' />{' '}
                                  Edit
                                </Dropdown.Item>
                              )}
                            </Dropdown.Menu>
                          </Dropdown>
                        </td>
                      )}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={hamburgerTableActions ? '8' : '7'} className='text-center py-4 nosearchResult'>
                      No records found.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>

          <div className='paginationSec'>
            <Pagination
              totalItems={totalItems}
              itemsPerPage={itemsPerPage}
              currentPage={currentPage}
              onPageChange={handlePageChange}
              siblingCount={2}
              onLimitChange={handleLimitChange}
              limitOptions={[10, 25, 50, 100, 500, 1000]}
              showSummary={true}
              showLimit={false}
            />
          </div>
        </Card.Body>
      </Card>
    </Container>
  )
}

const mapStateToProps = (globalState) => {
  return {
    userCredentials: globalState.LoginReducer.userCredentials,
    token: globalState.LoginReducer.token,
    userEntryType: globalState.LoginReducer.userEntryType,
    loaderState: globalState.mainReducerData.loaderState
  }
}

export default connect(mapStateToProps, { loaderStateTrue, loaderStateFalse, handleActiveLink })(withNamespaces()(KitchenDisplayList))
